import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useSEO, generateMedicalWebPageSchema } from '../../utils/seo';

const AwarenessPage = () => {
  // SEO Configuration
  useSEO({
    title: 'Cervical Cancer Awareness | Symptoms, Risk Factors & Early Detection',
    description: 'Comprehensive guide to cervical cancer awareness. Learn about symptoms, risk factors, HPV infection, and why early detection matters. 90%+ survival rate with early screening. Educational resources for women\'s health.',
    keywords: 'cervical cancer symptoms, cervical cancer risk factors, HPV infection, early detection, cancer awareness, women\'s health education',
    canonical: '/awareness',
    schema: generateMedicalWebPageSchema(
      'Cervical Cancer Awareness Guide',
      'Understanding cervical cancer, risk factors, symptoms, and the importance of early detection',
      '/awareness',
      ['Patient', 'General Public']
    ),
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="relative h-96 flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary to-teal-700"></div>
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/2d448e216cc19f10be5ecc871e8ffe16.jpg"
            alt="Awareness"
            className="w-full h-full object-cover object-top opacity-20"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-white">
          <h1 className="text-5xl md:text-6xl font-bold font-display mb-4">
            Cervical Cancer Awareness
          </h1>
          <p className="text-xl text-white/90 max-w-2xl">
            Knowledge is power. Understanding cervical cancer helps save lives through early detection and prevention.
          </p>
        </div>
      </section>

      <section className="py-24 bg-gradient-to-b from-white to-teal-50/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
            <div>
              <h2 className="text-4xl font-bold font-display text-gray-800 mb-6">
                What is <span className="text-primary">Cervical Cancer</span>?
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Cervical cancer is a type of cancer that occurs in the cells of the cervix  the lower part of the uterus that connects to the vagina. It develops slowly over time and is one of the most preventable types of cancer.
                </p>
                <p>
                  <strong>The main cause:</strong> Most cervical cancers are caused by persistent infection with high-risk types of human papillomavirus (HPV), a common virus transmitted through sexual contact.
                </p>
                <p>
                  <strong>Good news:</strong> When detected early through regular screening, cervical cancer is highly treatable. Precancerous changes can be identified and treated before cancer develops.
                </p>
              </div>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Medical%20illustration%20of%20female%20reproductive%20system%20showing%20cervix%20and%20uterus%20in%20educational%20style%2C%20clean%20professional%20healthcare%20diagram%20with%20teal%20and%20pink%20accents%2C%20simple%20background%20highlighting%20anatomical%20structure%20for%20medical%20education%20purposes&width=700&height=600&seq=cervix-anatomy-001&orientation=landscape"
                alt="Cervical Anatomy"
                className="w-full h-auto rounded-2xl shadow-xl"
              />
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-xl p-12 mb-24">
            <h2 className="text-3xl font-bold font-display text-gray-800 mb-8 text-center">
              Why Early Detection <span className="text-primary">Matters</span>
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  stat: '90%+',
                  label: 'Survival Rate',
                  description: 'When detected early, cervical cancer has a survival rate of over 90%',
                  icon: 'ri-heart-pulse-line',
                  color: 'green',
                },
                {
                  stat: '10-20',
                  label: 'Years to Develop',
                  description: 'Cervical cancer typically takes 10-20 years to develop, providing ample time for detection',
                  icon: 'ri-time-line',
                  color: 'blue',
                },
                {
                  stat: '100%',
                  label: 'Preventable',
                  description: 'With regular screening and HPV vaccination, cervical cancer is largely preventable',
                  icon: 'ri-shield-check-line',
                  color: 'teal',
                },
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className={`w-16 h-16 flex items-center justify-center bg-${item.color}-100 rounded-full mx-auto mb-4`}>
                    <i className={`${item.icon} text-3xl text-${item.color}-600`}></i>
                  </div>
                  <div className={`text-4xl font-bold text-${item.color}-600 mb-2`}>{item.stat}</div>
                  <div className="font-semibold text-gray-800 mb-2">{item.label}</div>
                  <p className="text-sm text-gray-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-24">
            <h2 className="text-3xl font-bold font-display text-gray-800 mb-12 text-center">
              Common <span className="text-primary">Risk Factors</span>
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: 'ri-virus-line',
                  title: 'HPV Infection',
                  description: 'Persistent infection with high-risk HPV types is the primary cause of cervical cancer',
                },
                {
                  icon: 'ri-smoke-line',
                  title: 'Smoking',
                  description: 'Tobacco use doubles the risk of cervical cancer and makes treatment less effective',
                },
                {
                  icon: 'ri-shield-cross-line',
                  title: 'Weakened Immune System',
                  description: 'HIV/AIDS or immunosuppressive medications can increase risk',
                },
                {
                  icon: 'ri-capsule-line',
                  title: 'Long-term Contraceptive Use',
                  description: 'Using oral contraceptives for 5+ years may slightly increase risk',
                },
                {
                  icon: 'ri-parent-line',
                  title: 'Multiple Pregnancies',
                  description: 'Having three or more full-term pregnancies increases risk',
                },
                {
                  icon: 'ri-calendar-line',
                  title: 'Early Sexual Activity',
                  description: 'First sexual intercourse before age 18 increases HPV exposure risk',
                },
              ].map((factor, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-primary"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-primary/10 rounded-lg mb-4">
                    <i className={`${factor.icon} text-2xl text-primary`}></i>
                  </div>
                  <h3 className="font-bold text-gray-800 mb-2">{factor.title}</h3>
                  <p className="text-sm text-gray-600">{factor.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-3xl p-12 mb-24">
            <h2 className="text-3xl font-bold font-display text-gray-800 mb-8 text-center">
              Symptoms & <span className="text-secondary">Warning Signs</span>
            </h2>
            <div className="bg-white rounded-2xl p-8 mb-8">
              <div className="flex items-start gap-4 mb-6">
                <i className="ri-information-line text-3xl text-blue-600"></i>
                <div>
                  <h3 className="font-bold text-gray-800 mb-2">Important Note</h3>
                  <p className="text-gray-700">
                    <strong>Early-stage cervical cancer often has no symptoms.</strong> This is why regular screening is crucial. Symptoms typically appear only when cancer has advanced.
                  </p>
                </div>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                'Abnormal vaginal bleeding (between periods, after intercourse, or after menopause)',
                'Unusual vaginal discharge that may be watery, bloody, or have a foul odor',
                'Pelvic pain or pain during sexual intercourse',
                'Bleeding after menopause',
                'Heavier or longer menstrual periods than usual',
                'Pain in the lower back or legs',
                'Unexplained weight loss',
                'Fatigue and loss of appetite',
              ].map((symptom, index) => (
                <div key={index} className="flex items-start gap-3 bg-white rounded-lg p-4">
                  <i className="ri-alert-line text-secondary text-xl mt-0.5 flex-shrink-0"></i>
                  <span className="text-gray-700">{symptom}</span>
                </div>
              ))}
            </div>
            <div className="mt-8 text-center">
              <p className="text-gray-700 mb-4">
                <strong>If you experience any of these symptoms, consult a healthcare provider immediately.</strong>
              </p>
              <Link
                to="/emergency"
                className="inline-flex items-center gap-2 px-6 py-3 bg-secondary text-white rounded-full font-semibold hover:bg-secondary/90 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                Emergency Guidance
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>

          <div className="text-center">
            <h2 className="text-3xl font-bold font-display text-gray-800 mb-6">
              Ready to Take Action?
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Learn about prevention strategies and screening recommendations
            </p>
            <Link
              to="/prevention"
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-primary to-teal-500 text-white rounded-full text-lg font-semibold hover:shadow-2xl hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              View Prevention Guide
              <i className="ri-arrow-right-line text-xl"></i>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AwarenessPage;
